<template>
  <CModal :title="title"
          color="info"
          :show="show"
          :centered="true"
          class="batch-user-add-modal"
          @update:show="handleCancel">

    <div>
      <div class="modal-title">
        <h2 class="">{{ title }}</h2>
        <i v-if="loading" class="fa fa-cog fa-spin fa-2x"></i>
      </div>

      <div v-if="errors.length" class="my-3">
        <CAlert v-for="(error, index) in errors" :key="`error-${index}`" :show="90" color="danger" closeButton>
          {{ error }}
        </CAlert>
      </div>

      <div v-if="uploadResults">
        <!-- successful results display -->
        <h4>Upload results:</h4>
        <pre>{{ JSON.stringify(uploadResults, null, 2) }}</pre>
      </div>
      <div v-else>
        <!-- form to add users -->
        <CSelect :value.sync="fields.organization.value" :options="fields.organization.choices">
          <template #description>
            <div v-if="!isOrganizationValid && fieldsErrorAllowed" class="field-error">
              Please select an organization.
            </div>
          </template>
        </CSelect>

        <div class="user-fields">
          <CInput
            ref="userNameCInput"
            type="text"
            v-model="fields.userName.value"
            @keydown.enter="handleUserAdd"
            :placeholder="fields.userName.placeholder">
            <template #description>
              <div v-if="!isUserNameValid && fieldsErrorAllowed" class="field-error">
                Please enter a valid name<br>
                at least {{ fields.userName.minLength }} symbols length.
              </div>
            </template>
          </CInput>

          <CInput
            type="email"
            v-model="fields.userEmail.value"
            @keydown.enter="handleUserAdd"
            :placeholder="fields.userEmail.placeholder">
            <template #description>
              <div v-if="!isUserEmailValid && fieldsErrorAllowed" class="field-error">
                Please enter a valid email<br>
                at least {{ fields.userEmail.minLength }} symbols length.
              </div>
            </template>
          </CInput>

          <div class="user-fields__submit">
            <CButton @click="handleUserAdd" color="success">
              Add user
            </CButton>
            <div class="field-description">
              Or press "Enter"
            </div>
          </div>
        </div>

        <div class="users-list">
          <div v-for="(user, userIndex) in users" :key="`user-${userIndex}`" class="user">
            <div class="user__field">
              <span class="pr-2">{{ userIndex+1 }}.</span>
              {{ user.name }}
            </div>
            <div class="user__field">{{ user.email }}</div>
            <div class="user__remove" @click="handleUserRemove(userIndex)">&times;</div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <template v-if="uploadResults">
        <CButton @click="handleCancel" color="secondary">Close</CButton>
      </template>
      <template v-else>
        <CButton @click="handleCancel" color="secondary">Cancel</CButton>
        <CButton @click="handleSave"
                 :disabled="!isDataValid"
                 color="success">
          Save
        </CButton>
      </template>
    </template>

  </CModal>
</template>

<script>
import { mapActions } from 'vuex';
import getNested from 'lodash-es/get';
import every from 'lodash-es/every';
import isEmpty from 'lodash-es/isEmpty';


export default {
  name: 'batch-users-add-modal',

  components: {
    // ...
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },

    title: {
      type: String,
      default: 'Batch users adding',
    },
  },

  data() {
    return {
      users: [],
      loading: false,
      errors: [],
      fieldsErrorAllowed: false,
      uploadResults: null,

      fields: {
        organization: {
          choices: [],
          defaultChoiceItem: {
            label: '-- Select an organization --',
            value: null,
            disabled: true,
          },
          value: null,
          error: null,
        },

        userName: {
          value: '',
          placeholder: 'User name',
          error: null,
          minLength: 5,
        },

        userEmail: {
          value: '',
          placeholder: 'email',
          error: null,
          minLength: 5,
        },
      },
    };
  },

  async mounted() {
    await this.loadOrganizations();
  },

  methods: {
    ...mapActions('users', ['listOrgs', 'uploadUsers']),

    async loadOrganizations() {
      let choices = [this.fields.organization.defaultChoiceItem];
      this.fields.organization.choices = choices.concat(
        // retrieved values
        getNested(await this.listOrgs(), 'organizations', [])
        .map(item => ({
          value: item.organizationId,
          label: `${item.organizationName}`,
        }))
      );
    },

    handleUserAdd() {
      this.fieldsErrorAllowed = true;
      const name = this.fields.userName.value.trim();
      const email = this.fields.userEmail.value.trim();
      const isAlreadyAdded = !!this.users.find(item => item.email === email);
      this.fields.userName.value = name;
      this.fields.userEmail.value = email;

      if(this.isUserNameValid && this.isUserEmailValid) {
        if(!isAlreadyAdded) {
          this.users.push({name, email});
        }
        this.fields.userName.value = '';
        this.fields.userEmail.value = '';
        this.fieldsErrorAllowed = false;
      }

      this.$refs.userNameCInput.$el.querySelector('input').focus();
    },

    handleUserRemove(userIndex) {
      this.users = this.users.filter((item, index) => index !== userIndex);
    },

    async handleSave(evt) {
      if(evt) {
        evt.preventDefault();
      }

      this.loading = true;

      const response = await this.uploadUsers({
        orgId: this.fields.organization.value,
        users: this.users,
      });

      if(response.error) {
        this.errors.push(`Can't upload users: ${JSON.stringify(response.error_details)}`);
      } else {
        this.$emit('updated');
        this.uploadResults = response;
        this.fieldsErrorAllowed = false;
        // this.$emit('update:show', false);
      }

      this.loading = false;
    },

    handleCancel() {
      this.$emit('update:show', false);
    },

    validateUserName(value) {
      return (
        !!value &&
        value.length >= this.fields.userName.minLength
      );
    },

    validateUserEmail(value) {
      return (
        !!value &&
        value.length >= this.fields.userEmail.minLength &&
        /\S+@\S+\.\S+/.test(value)
      )
    },

    resetModalState() {
      this.errors = [];
      this.users = [];
      this.fields.organization.value = null;
      this.fields.userName.value = '';
      this.fields.userEmail.value = '';
      this.fieldsErrorAllowed = false;
    },
  },

  computed: {
    isOrganizationValid() {
      return (
        !!this.fields.organization.value
      );
    },

    isUserNameValid() {
      return this.validateUserName(this.fields.userName.value);
    },

    isUserEmailValid() {
      return this.validateUserEmail(this.fields.userEmail.value);
    },

    isDataValid() {
      return (
        this.isOrganizationValid &&
        !isEmpty(this.users) &&
        every(
          this.users.map(item => {
            return (
              this.validateUserName(item.name) &&
              this.validateUserEmail(item.email)
            );
        }), Boolean)
      );
    },
  },

  watch: {
    async show(value) {
      if(value) {
        this.resetModalState();
        await this.loadOrganizations();
      }
    },
  },
}
</script>


<style scoped lang="scss">
.batch-user-add-modal {
  .modal-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .fa-cog {
      color: #adadad;
    }
  }

  .user-fields {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;

    .form-group {
      margin: 0;
    }

    &__submit {
      display: flex;
      flex-direction: column;
    }
  }

  .field-error {
    font-size: 12px;
    color: #660000;
    margin-left: 3px;
  }

  .field-description {
    font-size: 12px;
    color: gray;
  }

  .users-list {
    margin: 25px 0 10px 0;

    .user {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding: 0 10px;

      &:hover {
        background-color: #e8e8e8;
      }

      &__field {
        padding: 0 5px;
        width: 50%;
      }

      &__remove {
        margin-left: auto;
        font-size: 30px;
        line-height: 35px;
        font-weight: bold;
        color: #9a0000;
        cursor: pointer;
      }

      &__remove:hover {
        color: #bd0404;
      }
    }
  }
}
</style>
